// src/components/admin/page-management/section-forms/StaticContentForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import MediaLibrary from '../../shared/MediaUploader';

const StaticContentForm = ({ content, updateContent, pageSlug }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [activeTab, setActiveTab] = useState('content');

  const handleSelectImage = (imageUrl) => {
    updateContent('imageUrl', imageUrl);
    setShowMediaLibrary(false);
  };

  const InfoTooltip = ({ children, tooltip }) => (
    <div className="relative group inline-block">
      {children}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
        {tooltip}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );

  const FormSection = ({ title, icon, children, className = "" }) => (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 ${className}`}>
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
          {icon}
        </div>
        <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
      </div>
      {children}
    </div>
  );

  const TabButton = ({ active, onClick, children, icon }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
        active 
          ? 'bg-indigo-600 text-white shadow-md' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
      }`}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );

  const InputField = ({ label, type = "text", value, onChange, placeholder, tooltip, children, className = "" }) => (
    <div className={`space-y-2 ${className}`}>
      <label className="flex items-center text-sm font-medium text-gray-700">
        {label}
        {tooltip && (
          <InfoTooltip tooltip={tooltip}>
            <svg className="w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </InfoTooltip>
        )}
      </label>
      {children || (
        <input
          type={type}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 bg-white hover:border-gray-400"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </div>
  );

  const CheckboxField = ({ label, checked, onChange, tooltip, className = "" }) => (
    <div className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 ${className}`}>
      <div className="flex items-center">
        <div className={`w-4 h-4 rounded-full mr-3 transition-colors duration-200 ${checked ? 'bg-green-500' : 'bg-gray-300'}`}></div>
        <div>
          <label className="flex items-center font-medium text-gray-800 cursor-pointer">
            {label}
            {tooltip && (
              <InfoTooltip tooltip={tooltip}>
                <svg className="w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </InfoTooltip>
            )}
          </label>
          <p className="text-sm text-gray-600 mt-1">
            {checked ? 'Feature is enabled' : 'Feature is disabled'}
          </p>
        </div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
      </label>
    </div>
  );

  if (showMediaLibrary) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-medium">Media Library</h4>
          <button 
            onClick={() => setShowMediaLibrary(false)}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <MediaLibrary 
          onSelect={handleSelectImage}
          pageSlug={pageSlug}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-gray-900">Static Content Builder</h2>
        <p className="text-gray-600">Create engaging content sections with text, images, and call-to-action buttons</p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-3 justify-center">
        <TabButton 
          active={activeTab === 'content'} 
          onClick={() => setActiveTab('content')}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
            </svg>
          }
        >
          Content & Text
        </TabButton>
        <TabButton 
          active={activeTab === 'button'} 
          onClick={() => setActiveTab('button')}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          }
        >
          Button & Actions
        </TabButton>
        <TabButton 
          active={activeTab === 'image'} 
          onClick={() => setActiveTab('image')}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        >
          Image & Media
        </TabButton>
        <TabButton 
          active={activeTab === 'design'} 
          onClick={() => setActiveTab('design')}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
            </svg>
          }
        >
          Design & Layout
        </TabButton>
      </div>

      {/* Content & Text Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <FormSection 
            title="Content Settings" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            }
          >
            <div className="space-y-4">
              <InputField
                label="Section Title"
                value={content?.title}
                onChange={(e) => updateContent('title', e.target.value)}
                placeholder="Discover Our Unique Packaging Solutions"
                tooltip="Main heading that appears at the top of your content section"
              />

              <InputField label="Description" tooltip="Detailed text that appears below the title">
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 bg-white hover:border-gray-400 resize-none"
                  value={content?.description || ''}
                  onChange={(e) => updateContent('description', e.target.value)}
                  placeholder="Experience creativity and innovation in every design. Our custom packaging solutions are crafted to elevate your brand's presence and make a lasting impression."
                  rows="4"
                />
              </InputField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                  <ColorPicker
                    color={content?.textColor || '#ffffff'}
                    onChange={(color) => updateContent('textColor', color)}
                  />
                </div>

                <InputField label="Content Alignment" tooltip="How to align the text content">
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    value={content?.contentAlignment || 'left'}
                    onChange={(e) => updateContent('contentAlignment', e.target.value)}
                  >
                    <option value="left">Left Aligned</option>
                    <option value="center">Center Aligned</option>
                    <option value="right">Right Aligned</option>
                  </select>
                </InputField>
              </div>
            </div>
          </FormSection>
        </div>
      )}

      {/* Button & Actions Tab */}
      {activeTab === 'button' && (
        <div className="space-y-6">
          <FormSection 
            title="Button Configuration" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            }
          >
            <div className="space-y-4">
              <InputField
                label="Button Text"
                value={content?.buttonText}
                onChange={(e) => updateContent('buttonText', e.target.value)}
                placeholder="Learn More"
                tooltip="Text that appears on the button. Leave empty to hide the button completely"
              />
              <p className="text-xs text-gray-500">Leave empty to hide the button</p>

              {content?.buttonText && (
                <>
                  <InputField
                    label="Button Link"
                    value={content?.buttonLink}
                    onChange={(e) => updateContent('buttonLink', e.target.value)}
                    placeholder="/aboutUs"
                    tooltip="URL or path the button should link to"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Button Background Color</label>
                      <ColorPicker
                        color={content?.buttonColor || '#2563eb'}
                        onChange={(color) => updateContent('buttonColor', color)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Button Text Color</label>
                      <ColorPicker
                        color={content?.buttonTextColor || '#ffffff'}
                        onChange={(color) => updateContent('buttonTextColor', color)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </FormSection>
        </div>
      )}

      {/* Image & Media Tab */}
      {activeTab === 'image' && (
        <div className="space-y-6">
          <FormSection 
            title="Image Settings" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input
                  type="text"
                  value={content?.imageUrl || '/images/logo-static.png'}
                  onChange={(e) => updateContent('imageUrl', e.target.value)}
                  className="flex-1 p-2 border rounded"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(true)}
                  className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Browse
                </button>
              </div>

              {content?.imageUrl && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-800 mb-2">Image Preview</h5>
                  <div className="relative h-48 overflow-hidden rounded-lg border bg-white">
                    <img 
                      src={content.imageUrl} 
                      alt="Preview" 
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Image Alt Text"
                  value={content?.imageAlt}
                  onChange={(e) => updateContent('imageAlt', e.target.value)}
                  placeholder="Packaging"
                  tooltip="Alternative text for screen readers and SEO"
                />

                <InputField
                  label="Image Width"
                  value={content?.imageWidth}
                  onChange={(e) => updateContent('imageWidth', e.target.value)}
                  placeholder="500px"
                  tooltip="Width of the image (e.g., 500px, 100%, 30rem)"
                />

                <InputField
                  label="Image Border Radius"
                  value={content?.imageRadius}
                  onChange={(e) => updateContent('imageRadius', e.target.value)}
                  placeholder="50px"
                  tooltip="Rounded corners for the image (e.g., 8px, 50px, 0)"
                />
              </div>

              <CheckboxField
                label="Add Shadow to Image"
                checked={content?.imageShadow !== false}
                onChange={(e) => updateContent('imageShadow', e.target.checked)}
                tooltip="Adds a subtle drop shadow behind the image"
              />
            </div>
          </FormSection>
        </div>
      )}

      {/* Design & Layout Tab */}
      {activeTab === 'design' && (
        <div className="space-y-6">
          {/* Background Settings */}
          <FormSection 
            title="Background Styling" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <CheckboxField
                label="Use Gradient Background"
                checked={content?.backgroundGradient !== false}
                onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
                tooltip="Creates a smooth color transition instead of a solid color"
              />

              {content?.backgroundGradient !== false ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gradient Start Color</label>
                    <ColorPicker
                      color={content?.gradientFrom || '#3b82f6'}
                      onChange={(color) => updateContent('gradientFrom', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gradient End Color</label>
                    <ColorPicker
                      color={content?.gradientTo || '#8b5cf6'}
                      onChange={(color) => updateContent('gradientTo', color)}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                  <ColorPicker
                    color={content?.backgroundColor || '#3b82f6'}
                    onChange={(color) => updateContent('backgroundColor', color)}
                  />
                </div>
              )}

              <InputField
                label="Border Radius"
                value={content?.borderRadius}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
                placeholder="0.5rem"
                tooltip="Rounded corners for the entire section (e.g., 0.5rem, 8px, 0)"
              />
            </div>
          </FormSection>

          {/* Layout Settings */}
          <FormSection 
            title="Layout & Spacing" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <CheckboxField
                label="Reverse Layout on Mobile"
                checked={content?.reverseOnMobile === true}
                onChange={(e) => updateContent('reverseOnMobile', e.target.checked)}
                tooltip="Shows image first on mobile devices instead of text"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Top Padding"
                  value={content?.paddingTop}
                  onChange={(e) => updateContent('paddingTop', e.target.value)}
                  placeholder="2.5rem"
                  tooltip="Space above the content"
                />

                <InputField
                  label="Bottom Padding"
                  value={content?.paddingBottom}
                  onChange={(e) => updateContent('paddingBottom', e.target.value)}
                  placeholder="2.5rem"
                  tooltip="Space below the content"
                />

                <InputField
                  label="Left Padding (Desktop)"
                  value={content?.paddingLeftDesktop}
                  onChange={(e) => updateContent('paddingLeftDesktop', e.target.value)}
                  placeholder="40px"
                  tooltip="Left spacing on desktop screens"
                />

                <InputField
                  label="Right Padding (Desktop)"
                  value={content?.paddingRightDesktop}
                  onChange={(e) => updateContent('paddingRightDesktop', e.target.value)}
                  placeholder="0px"
                  tooltip="Right spacing on desktop screens"
                />

                <InputField
                  label="Top Margin"
                  value={content?.marginTop}
                  onChange={(e) => updateContent('marginTop', e.target.value)}
                  placeholder="0.5rem"
                  tooltip="Space above the entire section"
                />

                <InputField
                  label="Bottom Margin"
                  value={content?.marginBottom}
                  onChange={(e) => updateContent('marginBottom', e.target.value)}
                  placeholder="8rem"
                  tooltip="Space below the entire section"
                />
              </div>
            </div>
          </FormSection>

          {/* Animation Settings */}
          <FormSection 
            title="Animation Effects" 
            icon={
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <CheckboxField
                label="Enable Animation"
                checked={content?.animationEnabled !== false}
                onChange={(e) => updateContent('animationEnabled', e.target.checked)}
                tooltip="Adds smooth fade-in animation when the section comes into view"
              />

              {content?.animationEnabled !== false && (
                <InputField
                  label="Animation Delay (seconds)"
                  type="number"
                  value={content?.animationDelay}
                  onChange={(e) => updateContent('animationDelay', e.target.value)}
                  placeholder="0"
                  tooltip="How long to wait before starting the animation"
                />
              )}
            </div>
          </FormSection>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Quick Tips
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span>Use high-quality images for better visual impact</span>
          </div>
          <div className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span>Keep text concise and engaging</span>
          </div>
          <div className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span>Test your colors for good contrast and readability</span>
          </div>
          <div className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span>Use animations sparingly for better performance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticContentForm;
// 
// // src/components/admin/page-management/section-forms/StaticContentForm.js
// import React, { useState } from 'react';
// import ColorPicker from '../../shared/ColorPicker';
// import MediaLibrary from '../../shared/MediaUploader';

// const StaticContentForm = ({ content, updateContent, pageSlug }) => {
//   const [showMediaLibrary, setShowMediaLibrary] = useState(false);

//   const handleSelectImage = (imageUrl) => {
//     updateContent('imageUrl', imageUrl);
//     setShowMediaLibrary(false);
//   };

//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Static Content Section</h3>
      
//       {showMediaLibrary ? (
//         <div className="bg-white rounded-lg shadow-lg p-4">
//           <div className="flex justify-between items-center mb-4">
//             <h4 className="text-lg font-medium">Media Library</h4>
//             <button 
//               onClick={() => setShowMediaLibrary(false)}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               Close
//             </button>
//           </div>
//           <MediaLibrary 
//             onSelect={handleSelectImage}
//             pageSlug={pageSlug}
//           />
//         </div>
//       ) : (
//         <>
//           {/* Content Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Content</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Discover Our Unique Packaging Solutions'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Description</label>
//               <textarea
//                 value={content?.description || 'Experience creativity and innovation in every design. Our custom packaging solutions are crafted to elevate your brand\'s presence and make a lasting impression.'}
//                 onChange={(e) => updateContent('description', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 rows="4"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Text Color</label>
//               <ColorPicker
//                 color={content?.textColor || '#ffffff'}
//                 onChange={(color) => updateContent('textColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Content Alignment</label>
//               <select
//                 value={content?.contentAlignment || 'left'}
//                 onChange={(e) => updateContent('contentAlignment', e.target.value)}
//                 className="w-full p-2 border rounded"
//               >
//                 <option value="left">Left</option>
//                 <option value="center">Center</option>
//                 <option value="right">Right</option>
//               </select>
//             </div>
//           </div>
          
//           {/* Button Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Button</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.buttonText || 'Learn More'}
//                 onChange={(e) => updateContent('buttonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">Leave empty to hide the button</p>
//             </div>
            
//             {content?.buttonText && (
//               <>
//                 <div className="mb-4">
//                   <label className="block text-sm font-medium mb-1">Button Link</label>
//                   <input
//                     type="text"
//                     value={content?.buttonLink || '/aboutUs'}
//                     onChange={(e) => updateContent('buttonLink', e.target.value)}
//                     className="w-full p-2 border rounded"
//                   />
//                 </div>
                
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                   <div>
//                     <label className="block text-sm font-medium mb-1">Button Color</label>
//                     <ColorPicker
//                       color={content?.buttonColor || '#2563eb'}
//                       onChange={(color) => updateContent('buttonColor', color)}
//                     />
//                   </div>
//                   <div>
//                     <label className="block text-sm font-medium mb-1">Button Text Color</label>
//                     <ColorPicker
//                       color={content?.buttonTextColor || '#ffffff'}
//                       onChange={(color) => updateContent('buttonTextColor', color)}
//                     />
//                   </div>
//                 </div>
//               </>
//             )}
//           </div>
          
//           {/* Image Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Image</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Image</label>
//               <div className="flex items-center">
//                 <input
//                   type="text"
//                   value={content?.imageUrl || '/images/logo-static.png'}
//                   onChange={(e) => updateContent('imageUrl', e.target.value)}
//                   className="flex-1 p-2 border rounded"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => setShowMediaLibrary(true)}
//                   className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//                 >
//                   Browse
//                 </button>
//               </div>
              
//               {content?.imageUrl && (
//                 <div className="mt-2 relative h-32 overflow-hidden rounded border">
//                   <img 
//                     src={content.imageUrl} 
//                     alt="Preview" 
//                     className="h-full w-auto object-contain mx-auto"
//                   />
//                 </div>
//               )}
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Image Alt Text</label>
//               <input
//                 type="text"
//                 value={content?.imageAlt || 'Packaging'}
//                 onChange={(e) => updateContent('imageAlt', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Image Width</label>
//               <input
//                 type="text"
//                 value={content?.imageWidth || '500px'}
//                 onChange={(e) => updateContent('imageWidth', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., 500px, 100%, 30rem"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Image Radius</label>
//               <input
//                 type="text"
//                 value={content?.imageRadius || '50px'}
//                 onChange={(e) => updateContent('imageRadius', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., 8px, 50px, 0"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.imageShadow !== false}
//                   onChange={(e) => updateContent('imageShadow', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Add Shadow to Image</span>
//               </label>
//             </div>
//           </div>
          
//           {/* Background Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Background</h4>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.backgroundGradient !== false}
//                   onChange={(e) => updateContent('backgroundGradient', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Use Gradient Background</span>
//               </label>
//             </div>
            
//             {content?.backgroundGradient !== false ? (
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                 <div>
//                   <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
//                   <ColorPicker
//                     color={content?.gradientFrom || '#3b82f6'}
//                     onChange={(color) => updateContent('gradientFrom', color)}
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium mb-1">Gradient End Color</label>
//                   <ColorPicker
//                     color={content?.gradientTo || '#8b5cf6'}
//                     onChange={(color) => updateContent('gradientTo', color)}
//                   />
//                 </div>
//               </div>
//             ) : (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Background Color</label>
//                 <ColorPicker
//                   color={content?.backgroundColor || '#3b82f6'}
//                   onChange={(color) => updateContent('backgroundColor', color)}
//                 />
//               </div>
//             )}
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Border Radius</label>
//               <input
//                 type="text"
//                 value={content?.borderRadius || '0.5rem'}
//                 onChange={(e) => updateContent('borderRadius', e.target.value)}
//                 className="w-full p-2 border rounded"
//                 placeholder="e.g., 0.5rem, 8px, 0"
//               />
//             </div>
//           </div>
          
//           {/* Layout Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Layout</h4>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.reverseOnMobile === true}
//                   onChange={(e) => updateContent('reverseOnMobile', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Reverse Layout (Image First on Mobile)</span>
//               </label>
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Padding Top</label>
//                 <input
//                   type="text"
//                   value={content?.paddingTop || '2.5rem'}
//                   onChange={(e) => updateContent('paddingTop', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 2.5rem, 40px"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Padding Bottom</label>
//                 <input
//                   type="text"
//                   value={content?.paddingBottom || '2.5rem'}
//                   onChange={(e) => updateContent('paddingBottom', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 2.5rem, 40px"
//                 />
//               </div>
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Left Padding (Desktop)</label>
//                 <input
//                   type="text"
//                   value={content?.paddingLeftDesktop || '40px'}
//                   onChange={(e) => updateContent('paddingLeftDesktop', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 40px, 2.5rem"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Right Padding (Desktop)</label>
//                 <input
//                   type="text"
//                   value={content?.paddingRightDesktop || '0px'}
//                   onChange={(e) => updateContent('paddingRightDesktop', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 0px, 2rem"
//                 />
//               </div>
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Margin Top</label>
//                 <input
//                   type="text"
//                   value={content?.marginTop || '0.5rem'}
//                   onChange={(e) => updateContent('marginTop', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 0.5rem, 8px"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//                 <input
//                   type="text"
//                   value={content?.marginBottom || '8rem'}
//                   onChange={(e) => updateContent('marginBottom', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 8rem, 128px"
//                 />
//               </div>
//             </div>
//           </div>
          
//           {/* Animation Settings */}
//           <div className="bg-gray-50 p-4 rounded-md">
//             <h4 className="font-medium mb-4">Animation</h4>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.animationEnabled !== false}
//                   onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Enable Animation</span>
//               </label>
//             </div>
            
//             {content?.animationEnabled !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Animation Delay (seconds)</label>
//                 <input
//                   type="number"
//                   min="0"
//                   step="0.1"
//                   value={content?.animationDelay || '0'}
//                   onChange={(e) => updateContent('animationDelay', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 />
//               </div>
//             )}
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default StaticContentForm;